import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCoins, 
  FaArrowLeft, 
  FaInfoCircle, 
  FaBolt, 
  FaMusic, 
  FaCheckCircle
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import style from './AIPolicyAppendix4.module.css';

const AIPolicyAppendix4 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className={style.pageContainer}>
      <header className={style.header}>
        <div className={style.headerContent}>
          <Link to="/ai-policy" className={style.backLink}>
            <FaArrowLeft /> Politika Belgesine Dön
          </Link>
          <div className={style.badge}>
            <FaCoins className={style.badgeIcon} />
            <span>EK–4: YAPAY ZEKÂ KREDİ SİSTEMİ</span>
          </div>
          <h1 className={style.title}>Talent14 Yapay Zekâ <br/> Kredi Sistemi Rehberi</h1>
          <p className={style.subtitle}>
            Yapay zekâ servislerinin çalışma prensipleri, model bazlı maliyet yapıları ve şeffaf tüketim çerçevesi.
          </p>
        </div>
      </header>

      <main className={style.mainContent}>
        <section className={style.introSection}>
          <p>Bu belge, Talent14 Yapay Zekâ Politika Belgesi’nin ayrılmaz bir parçası olarak hazırlanmış EK-4 dokümanıdır.</p>
          <p>Bu ekte; Talent14 platformunda kullanılan yapay zekâ servislerinin kredi bazlı çalışma prensipleri, tüketim türleri ve model bazlı maliyet yapısı şeffaf ve denetlenebilir şekilde tanımlanmaktadır.</p>
        </section>

        <motion.section 
          className={style.contentSection}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section 1: Amac ve Kapsam */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>1</span>
              <h2>Amaç ve Kapsam</h2>
            </div>
            <div className={style.cardBody}>
              <ul className={style.checkList}>
                <li><FaCheckCircle /> Adil ve sürdürülebilir kullanım sağlamak.</li>
                <li><FaCheckCircle /> Kredi tüketimini önceden öngörülebilir kılmak.</li>
                <li><FaCheckCircle /> Şeffaf bir maliyet çerçevesi sunmak.</li>
              </ul>
              <div className={style.noteBox}>
                <FaInfoCircle /> <span>Bu doküman; AI14, CodeAI, AI Asist ve AI Tool bileşenlerini kapsar.</span>
              </div>
            </div>
          </motion.div>

          {/* Section 2: AI Kredisi Tanimi */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>2</span>
              <h2>AI Kredisi Tanımı</h2>
            </div>
            <div className={style.cardBody}>
              <p>AI kredisi, Talent14 platformunda sunulan tüm yapay zekâ hizmetleri için geçerli olan ortak ve standart tüketim birimidir.</p>
              <div className={style.highlightGrid}>
                <div className={style.hItem}>Farklı işlemler farklı hesaplama gücü (GPU/CPU) gerektirir.</div>
                <div className={style.hItem}>Kredi sistemi bu farkı ölçülebilir ve karşılaştırılabilir hâle getirir.</div>
                <div className={style.hItem}>Her işlem öncesinde tahmini tüketim açıkça gösterilir.</div>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Text ve Kod */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>3</span>
              <h2>Text ve Kod Üretimi (Token Bazlı)</h2>
            </div>
            <div className={style.cardBody}>
              <div className={style.subSection}>
                <h3>3.1 Hesaplama Esası</h3>
                <div className={style.tokenCalc}>
                  <div className={style.calcBox}>
                    <strong>Standart Referans Model (X)</strong>
                    <span>1000 Token = 1 Kredi</span>
                  </div>
                  <p>Yüksek kapasiteli modellerde maliyet çarpanı (X2, X3, X5 vb.) uygulanır ve seçim ekranında gösterilir.</p>
                </div>
              </div>
              <div className={style.subSection}>
                <h3>3.2 Chat Alanı – Ücretsiz Modeller</h3>
                <p>Aşağıdaki modellerle yapılan temel destek chat görüşmelerinde kredi tüketimi gerçekleşmez:</p>
                <div className={style.freeModels}>
                  <div className={style.modelTag}>Gemini 2.0 Flash</div>
                  <div className={style.modelTag}>GPT-4.0 Nano</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4 & 5: Assist & Tool */}
          <div className={style.gridRow}>
            <motion.div variants={itemVariants} className={style.infoCard}>
              <div className={style.cardHeader}>
                <span className={style.num}>4</span>
                <h2>AI Asist</h2>
              </div>
              <div className={style.cardBody}>
                <p>Bağlamsal ve kişiselleştirilmiş yardımcı bileşen.</p>
                <div className={style.priceRow}>
                  <span>Text tabanlı</span><strong>1 Kredi</strong>
                </div>
                <div className={style.priceRow}>
                  <span>Image tabanlı</span><strong>3 Kredi</strong>
                </div>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className={style.infoCard}>
              <div className={style.cardHeader}>
                <span className={style.num}>5</span>
                <h2>AI Tool</h2>
              </div>
              <div className={style.cardBody}>
                <p>Önceden yapılandırılmış hazır eğitim araçları.</p>
                <div className={style.priceRow}>
                  <span>1 AI Tool kullanımı</span><strong>5 Kredi</strong>
                </div>
                <div className={style.toolList}>
                  Ders planı, Etkinlik tasarımı, Özetleme vb.
                </div>
              </div>
            </motion.div>
          </div>

          {/* Section 6: Gorsel Uretimi Table */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>6</span>
              <h2>Görsel Üretimi (Sabit Kredi)</h2>
            </div>
            <div className={style.cardBody}>
              <div className={style.tableWrapper}>
                <table className={style.costTable}>
                  <thead>
                    <tr>
                      <th>Görsel Model</th>
                      <th>Kredi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={style.recommended}>
                      <td>OpenAI: GPT-5 Image Mini (Önerilen)</td>
                      <td>20</td>
                    </tr>
                    <tr>
                      <td>Google: Gemini 2.5 Flash</td>
                      <td>70</td>
                    </tr>
                    <tr>
                      <td>OpenAI: GPT-5 Image</td>
                      <td>100</td>
                    </tr>
                    <tr>
                      <td>Flux.2 Flex</td>
                      <td>100</td>
                    </tr>
                    <tr>
                      <td>Google: Gemini 3 Pro</td>
                      <td>280</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Section 7: Video Uretimi Table */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>7</span>
              <h2>Video Üretimi (4–6 sn, Sabit Kredi)</h2>
            </div>
            <div className={style.cardBody}>
              <div className={style.tableWrapper}>
                <table className={style.costTable}>
                  <thead>
                    <tr>
                      <th>Video Modeli</th>
                      <th>Ses</th>
                      <th>Kredi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Longcat 480px</td><td>Sessiz</td><td>350</td></tr>
                    <tr><td>Longcat 720px</td><td>Sessiz</td><td>560</td></tr>
                    <tr><td>Hailou-02 Standard</td><td>Sessiz</td><td>630</td></tr>
                    <tr><td>Kling 2.5 Turbo</td><td>Sessiz</td><td>980</td></tr>
                    <tr><td>Sora 2</td><td>Sesli</td><td>1400</td></tr>
                    <tr><td>Veo-3</td><td>Sessiz</td><td>1400</td></tr>
                    <tr><td>Veo-3</td><td>Sesli</td><td>2100</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Section 8: Ses ve Muzik */}
          <motion.div variants={itemVariants} className={style.infoCard}>
            <div className={style.cardHeader}>
              <span className={style.num}>8</span>
              <h2>Ses ve Müzik Üretimi</h2>
            </div>
            <div className={style.cardBody}>
              <div className={style.audioFlex}>
                <div className={style.audioItem}>
                  <FaBolt />
                  <div>
                    <strong>Text → Audio</strong>
                    <p>30 karakter = 1 Kredi</p>
                  </div>
                </div>
                <div className={style.audioItem}>
                  <FaMusic />
                  <div>
                    <strong>Text → Music</strong>
                    <p>90 sn müzik = 210 Kredi</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className={style.finalNote}>
            📌 Bu EK-4 dokümanı, Talent14 Yapay Zekâ Politika Belgesi ile birlikte değerlendirilir ve platform genelinde bağlayıcıdır.
          </div>
        </motion.section>
      </main>

      <footer className={style.pageFooter}>
        <p>© {new Date().getFullYear()} Talent14 - Şeffaf Kredi Yönetimi</p>
      </footer>
    </div>
  );
};

export default AIPolicyAppendix4;
